import { apiList, apiCreate, apiUpdate, apiDelete } from './apiClient';

export type ScopeType = 'standard' | 'online' | 'dealer' | 'customer' | 'property';
export type BillingStrategy = 'counter' | 'flat_rate' | 'tiered' | 'invoice';

export interface BillingRate {
  id: number;
  scope_type: ScopeType;
  scope_id: number | null;
  service_key: string;
  unit_price_cents: number;
  description: string;
  billing_strategy: BillingStrategy;
  flat_rate_cents?: number;        // For flat_rate strategy: total monthly price
  tier_thresholds?: TierThreshold[]; // For tiered strategy
  active: boolean;
}

export interface TierThreshold {
  min: number;
  max: number | null; // null = unlimited
  price_cents: number;
}

// Default standard rates (fallback if no DB records exist)
export const DEFAULT_RATES: Record<string, { cents: number; description: string; category: string }> = {
  per_unit: { cents: 150, description: 'Per unit per month', category: 'Core' },
  per_device: { cents: 500, description: 'Per standard device per month', category: 'Core' },
  per_premium_device: { cents: 1000, description: 'Per premium device (smart lock) per month', category: 'Core' },
  per_phone: { cents: 300, description: 'Per phone number per month', category: 'Voice' },
  per_property_base: { cents: 1000, description: 'Base fee per property per month', category: 'Core' },
  per_talk_minute: { cents: 5, description: 'Per talk minute', category: 'Voice' },
  per_video_minute: { cents: 10, description: 'Per video minute', category: 'Video' },
  per_bridge_relay: { cents: 500, description: 'Per Bridge Relay channel per month', category: 'Hardware' },
  per_mobile_user: { cents: 0, description: 'Per mobile app user per month', category: 'Mobile' },
  per_guest_pin: { cents: 0, description: 'Per guest PIN per month', category: 'Access' },
  per_kiosk: { cents: 2000, description: 'Per kiosk device per month', category: 'Hardware' },
};

export const SERVICE_LABELS: Record<string, string> = {
  per_unit: 'Unit',
  per_device: 'Device',
  per_premium_device: 'Premium Device (Smart Lock)',
  per_phone: 'Phone Number',
  per_property_base: 'Property Base Fee',
  per_talk_minute: 'Talk Minute',
  per_video_minute: 'Video Minute',
  per_bridge_relay: 'Bridge Relay',
  per_mobile_user: 'Mobile User',
  per_guest_pin: 'Guest PIN',
  per_kiosk: 'Kiosk',
};

export const STRATEGY_LABELS: Record<BillingStrategy, string> = {
  counter: 'Per-Unit Counter',
  flat_rate: 'Flat Rate',
  tiered: 'Tiered Volume',
  invoice: 'Manual Invoice',
};

let _ratesCache: BillingRate[] | null = null;
let _cacheTime = 0;
const CACHE_TTL = 30000; // 30s

export async function getAllRates(): Promise<BillingRate[]> {
  if (_ratesCache && Date.now() - _cacheTime < CACHE_TTL) return _ratesCache;
  try {
    _ratesCache = await apiList('billing_rates');
    _cacheTime = Date.now();
    return _ratesCache;
  } catch {
    return [];
  }
}

export function invalidateRatesCache() {
  _ratesCache = null;
}

export async function getRates(scopeType: ScopeType, scopeId?: number | string): Promise<BillingRate[]> {
  const all = await getAllRates();
  return all.filter((r: any) =>
    r.scope_type === scopeType &&
    (scopeId ? r.scope_id === scopeId : !r.scope_id)
  );
}

export async function setRate(
  scopeType: ScopeType,
  scopeId: number | string | null,
  serviceKey: string,
  priceCents: number,
  options?: { description?: string; billing_strategy?: BillingStrategy; flat_rate_cents?: number; tier_thresholds?: TierThreshold[] }
): Promise<BillingRate> {
  invalidateRatesCache();
  const existing = await getRates(scopeType, scopeId || undefined);
  const found = existing.find(r => r.service_key === serviceKey);

  const data: any = { unit_price_cents: priceCents };
  if (options?.description) data.description = options.description;
  if (options?.billing_strategy) data.billing_strategy = options.billing_strategy;
  if (options?.flat_rate_cents != null) data.flat_rate_cents = options.flat_rate_cents;
  if (options?.tier_thresholds) data.tier_thresholds = JSON.stringify(options.tier_thresholds);

  if (found) {
    return apiUpdate('billing_rates', (found as any).uuid || found.id, data);
  }

  return apiCreate('billing_rates', {
    scope_type: scopeType,
    scope_id: scopeId,
    service_key: serviceKey,
    unit_price_cents: priceCents,
    description: options?.description || DEFAULT_RATES[serviceKey]?.description || serviceKey,
    billing_strategy: options?.billing_strategy || 'counter',
    active: true,
    ...data,
  });
}

export async function deleteRate(rateId: number): Promise<void> {
  invalidateRatesCache();
  await apiDelete('billing_rates', rateId);
}

/**
 * Resolve the effective rate for a service at a given scope.
 * Walks: property → customer → dealer → online → standard → DEFAULT_RATES
 */
export async function resolveRate(
  serviceKey: string,
  propertyId?: number | string,
  customerId?: number | string,
  dealerId?: number | string
): Promise<{ cents: number; source: ScopeType; sourceId?: number | string; strategy: BillingStrategy }> {
  const allRates = await getAllRates();

  const find = (type: ScopeType, id?: number | string) =>
    allRates.find(r => r.scope_type === type && (id ? r.scope_id === id : !r.scope_id) && r.service_key === serviceKey && r.active);

  if (propertyId) {
    const r = find('property', propertyId);
    if (r) return { cents: r.unit_price_cents, source: 'property', sourceId: propertyId, strategy: r.billing_strategy || 'counter' };
  }
  if (customerId) {
    const r = find('customer', customerId);
    if (r) return { cents: r.unit_price_cents, source: 'customer', sourceId: customerId, strategy: r.billing_strategy || 'counter' };
  }
  if (dealerId) {
    const r = find('dealer', dealerId);
    if (r) return { cents: r.unit_price_cents, source: 'dealer', sourceId: dealerId, strategy: r.billing_strategy || 'counter' };
  }
  const online = find('online');
  if (online) return { cents: online.unit_price_cents, source: 'online', strategy: online.billing_strategy || 'counter' };

  const standard = find('standard');
  if (standard) return { cents: standard.unit_price_cents, source: 'standard', strategy: standard.billing_strategy || 'counter' };

  return { cents: DEFAULT_RATES[serviceKey]?.cents || 0, source: 'standard', strategy: 'counter' };
}

export async function resolveAllRates(
  propertyId?: number | string,
  customerId?: number | string,
  dealerId?: number | string
): Promise<Record<string, { cents: number; source: ScopeType; sourceId?: number | string; strategy: BillingStrategy }>> {
  const result: Record<string, { cents: number; source: ScopeType; sourceId?: number | string; strategy: BillingStrategy }> = {};
  for (const key of Object.keys(DEFAULT_RATES)) {
    result[key] = await resolveRate(key, propertyId, customerId, dealerId);
  }
  return result;
}

/**
 * Check if a customer/property is on flat rate billing
 */
export async function getFlatRate(scopeType: ScopeType, scopeId: number | string): Promise<number | null> {
  const rates = await getRates(scopeType, scopeId);
  const flatRate = rates.find(r => r.billing_strategy === 'flat_rate' && r.service_key === 'flat_rate_total');
  return flatRate?.flat_rate_cents ?? null;
}

export async function setFlatRate(scopeType: ScopeType, scopeId: number | string, totalCents: number): Promise<void> {
  invalidateRatesCache();
  await setRate(scopeType, scopeId, 'flat_rate_total', totalCents, {
    description: 'Flat rate - all services included',
    billing_strategy: 'flat_rate',
    flat_rate_cents: totalCents,
  });
}
